import { TbFlame, TbInfoCircle, TbStar, TbTrophy } from "react-icons/tb";
import HeadlineStatCard, {
  HeadlineStatCardFooter,
  HeadlineStatCardFooterRow,
} from "../HeadlineStatCard/HeadlineStatCard";
import StatSplitBar from "../StatSplitBar/StatSplitBar";
import StatLabelInfoTooltip from "../StatLabelInfoTooltip/StatLabelInfoTooltip";
import {
  AllTimeStudentStats,
  EMPTY_PLATFORM_SUMMARY,
  formatPeerDiff,
  PlatformStreakStats,
  platformSummaryToStreakStats,
} from "../../constants/dashboardStats";
import "./AllTimeStatsStrip.scss";

type AllTimeStatsStripProps = {
  stats: AllTimeStudentStats;
  platformStreak?: PlatformStreakStats;
};

const formatStreakDays = (count: number) => `${count} ${count === 1 ? "day" : "days"}`;

const AllTimeStatsStrip = ({
  stats,
  platformStreak = platformSummaryToStreakStats(EMPTY_PLATFORM_SUMMARY),
}: AllTimeStatsStripProps) => {
  const correctPercent = Math.round(
    (stats.correctAnswers / (stats.correctAnswers + stats.incorrectAnswers)) * 100
  );
  const peerDiff = formatPeerDiff(stats.allTimeAvgScore, stats.peerAvg);

  return (
    <div className="all-time-stats-strip">
      <HeadlineStatCard
        label="Total done"
        labelInfo={
          <StatLabelInfoTooltip id="all-time-total-done-stats-tooltip" />
        }
        value={stats.totalDone.toLocaleString()}
        subtitle="questions completed"
        footer={
          <StatSplitBar
            correctPercent={correctPercent}
            correctLabel={`${stats.correctAnswers.toLocaleString()} correct`}
            incorrectLabel={`${stats.incorrectAnswers.toLocaleString()} incorrect`}
          />
        }
      />

      <HeadlineStatCard
        label="All-time avg score"
        labelInfo={
          <StatLabelInfoTooltip id="all-time-avg-score-stats-tooltip" />
        }
        value={`${stats.allTimeAvgScore}%`}
        subtitle="across all attempts"
        footer={
          <HeadlineStatCardFooter block>
            <HeadlineStatCardFooterRow
              icon={<TbStar />}
              iconClassName="headline-stat-card__icon--star"
            >
              {stats.starredQuestions} starred for review
            </HeadlineStatCardFooterRow>
            <HeadlineStatCardFooterRow>
              Peer avg: {stats.peerAvg}% ·{" "}
              <span style={{ color: peerDiff.color, fontWeight: 500 }}>
                {peerDiff.text}
              </span>{" "}
              vs peers
            </HeadlineStatCardFooterRow>
          </HeadlineStatCardFooter>
        }
      />

      <HeadlineStatCard
        className="headline-stat-card--question-bank-streak"
        label="Question bank streak"
        labelInfo={
          <span className="headline-stat-card__label-info-wrap">
            <button
              type="button"
              className="headline-stat-card__label-info"
              aria-describedby="question-bank-streak-tooltip"
            >
              <TbInfoCircle aria-hidden="true" />
            </button>
            <div
              id="question-bank-streak-tooltip"
              className="headline-stat-card__label-tooltip"
              role="tooltip"
            >
              Consecutive days you&apos;ve practised in this question bank. Your platform
              streak in the top bar (with the flame) tracks activity across all banks.
            </div>
          </span>
        }
        value={formatStreakDays(stats.currentStreak)}
        subtitle="in this question bank"
        footer={
          <HeadlineStatCardFooter block>
            <HeadlineStatCardFooterRow
              icon={<TbTrophy />}
              iconClassName="headline-stat-card__icon--trophy"
            >
              Best in this bank: {formatStreakDays(stats.bestStreak)}
            </HeadlineStatCardFooterRow>
            <HeadlineStatCardFooterRow
              icon={<TbFlame />}
              iconClassName="headline-stat-card__icon--platform-streak"
            >
              Platform streak: {formatStreakDays(platformStreak.current)} · best:{" "}
              {formatStreakDays(platformStreak.best)}
            </HeadlineStatCardFooterRow>
          </HeadlineStatCardFooter>
        }
      />
    </div>
  );
};

export default AllTimeStatsStrip;
